import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Field, useAsync, useToast } from '../components/ui';
import { api, Me } from '../lib/api';
import { applyTheme, readTheme, Theme, useSession } from '../lib/session';
import { PilotSettingsCard } from './pilot/PilotSettingsCard';
import { t } from '../lib/i18n';

export function SettingsPage() {
  const { me, setMe } = useSession();
  const toast = useToast();
  const [name, setName] = useState(me?.workspace.name ?? '');
  const [website, setWebsite] = useState(me?.workspace.website ?? '');
  const [theme, setTheme] = useState<Theme>(readTheme());
  const admin = me?.user.role === 'owner' || me?.user.role === 'admin';
  if (!me) return null;

  async function save() {
    try {
      setMe(await api.patch<Me>('/workspace', { name, website }));
      toast(t('Сохранено'));
    } catch (e) {
      toast((e as Error).message, 'bad');
    }
  }

  return (
    <>
      <div className="page-head">
        <div className="grow">
          <h1>{t('Настройки')}</h1>
          <p>{t('Рабочее пространство, внешний вид и доступ.')}</p>
        </div>
      </div>
      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
        <div className="card card-pad stack">
          <h3>{t('Рабочее пространство')}</h3>
          <Field label={t('Название')}>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} disabled={!admin} />
          </Field>
          <Field label={t('Сайт')}>
            <input className="input" value={website} onChange={(e) => setWebsite(e.target.value)} disabled={!admin} placeholder="https://example.com" />
          </Field>
          <div className="small muted">{t('ID:')} <code>{me.workspace.id}</code></div>
          {admin && <div><button className="btn btn-primary" onClick={save}>{t('Сохранить')}</button></div>}
        </div>
        <div className="card card-pad stack">
          <h3>{t('Внешний вид')}</h3>
          <div className="chips">
            {([
              ['system', 'Как в системе'],
              ['light', '☀️ Мелководье'],
              ['dark', '🌑 Глубина'],
            ] as [Theme, string][]).map(([v, l]) => (
              <button key={v} className={`chip${theme === v ? ' on' : ''}`} onClick={() => (setTheme(v), applyTheme(v))}>{t(l)}</button>
            ))}
          </div>
          <h3 style={{ marginTop: 12 }}>{t('Профиль')}</h3>
          <div className="stack small" style={{ gap: 4 }}>
            <div><span className="muted">{t('Имя:')}</span> {me.user.name}</div>
            <div><span className="muted">Email:</span> {me.user.email}</div>
            <div><span className="muted">{t('Роль:')}</span> {me.user.role}</div>
          </div>
        </div>
        <div className="card card-pad stack">
          <h3>{t('Политика безопасности')}</h3>
          <label className="row" style={{ cursor: me.user.role === 'owner' ? 'pointer' : 'default', alignItems: 'flex-start' }}>
            <input
              type="checkbox"
              checked={me.workspace.require_2fa}
              disabled={me.user.role !== 'owner'}
              onChange={async (e) => {
                try {
                  setMe(await api.patch<Me>('/workspace', { require_2fa: e.target.checked }));
                  toast(e.target.checked ? t('Теперь 2FA обязателен для всех') : t('Требование 2FA снято'));
                } catch (err) {
                  toast((err as Error).message.includes('first') ? t('Сначала включите 2FA себе в разделе «Безопасность»') : (err as Error).message, 'bad');
                }
              }}
            />
            <span>
              <b>{t('Требовать двухфакторный вход от всех участников')}</b>
              <div className="small muted">{t('Участники без 2FA увидят только страницу настройки, пока не включат его. Меняет только владелец.')}</div>
            </span>
          </label>
        </div>
        <JourneySettingsCard admin={admin && !me.support} />
        <PilotSettingsCard />
        <div className="card card-pad stack">
          <h3>{t('Онбординг')}</h3>
          <p className="muted">{t('Пройдено шагов: {done} из {total}.', { done: me.workspace.onboarding.completed.length, total: me.workspace.onboarding.steps.length })}</p>
          {admin ? (
            <div><Link className="btn" to="/onboarding">{t('Открыть мастер погружения')}</Link></div>
          ) : (
            <p className="small muted">{t('Настройку пространства проводят владелец или администратор.')}</p>
          )}
          <h3 style={{ marginTop: 12 }}>{t('Отправка')}</h3>
          <p className="small muted">
            {t('Провайдер:')} <code>{me.platform.delivery_provider}</code>
            {me.platform.delivery_provider === 'sandbox' && t(' — песочница: письма проходят весь путь, но не покидают MailOcean.')}
          </p>
        </div>
      </div>
    </>
  );
}

/**
 * Journeys of the workspace (Build 3): the frequency cap of journey marketing and the time zone used for contacts
 * without their own (quiet hours, «до 10:00 по времени контакта», date triggers). Owner/admin change them.
 */
function JourneySettingsCard({ admin }: { admin: boolean }) {
  const toast = useToast();
  const s = useAsync(() => api.get<{ frequency_cap: { max: number; per_days: number }; default_timezone: string }>('/journeys-settings'));
  const [max, setMax] = useState(3);
  const [days, setDays] = useState(7);
  const [tz, setTz] = useState('UTC');
  useEffect(() => {
    if (!s.data) return;
    setMax(s.data.frequency_cap.max);
    setDays(s.data.frequency_cap.per_days);
    setTz(s.data.default_timezone);
  }, [s.data]);
  const zones = (() => {
    try {
      return (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.('timeZone') ?? [];
    } catch {
      return [];
    }
  })();
  async function save() {
    try {
      await api.put('/journeys-settings', { frequency_cap: { max, per_days: days }, default_timezone: tz.trim() });
      toast(t('Сохранено'));
      s.reload();
    } catch (e) {
      toast((e as Error).message, 'bad');
    }
  }
  return (
    <div className="card card-pad stack">
      <h3>{t('Сценарии')}</h3>
      <Field label={t('Частотный лимит маркетинга сценариев')} hint={t('Не больше N писем сценариев одному контакту за период; сценарий может только ужесточить')}>
        <div className="inline-fields row wrap">
          <input className="input" style={{ width: 80 }} type="number" min={1} max={100} value={max} onChange={(e) => setMax(Number(e.target.value))} disabled={!admin} aria-label={t('Писем')} />
          <span className="small muted">{t('писем за')}</span>
          <input className="input" style={{ width: 80 }} type="number" min={1} max={90} value={days} onChange={(e) => setDays(Number(e.target.value))} disabled={!admin} aria-label={t('Дней')} />
          <span className="small muted">{t('дн.')}</span>
        </div>
      </Field>
      <Field label={t('Часовой пояс пространства')} hint={t('Для контактов без своего пояса: тихие часы, «до 10:00 по времени контакта», триггеры по дате')}>
        <input className="input input-mono" list="tz-list" value={tz} onChange={(e) => setTz(e.target.value)} disabled={!admin} placeholder="Europe/Riga" />
      </Field>
      <datalist id="tz-list">{zones.map((z) => <option key={z} value={z} />)}</datalist>
      {admin && <div><button className="btn btn-primary" onClick={save} disabled={!s.data}>{t('Сохранить')}</button></div>}
    </div>
  );
}
